"use client";

import Image from "next/image";
import Link from "next/link";
import {
  buildWebOfferingPath,
  type ShareChristmasOfferingSummary,
} from "@/lib/christmas/shareBundleChristmas";
import { formatOfferingPrice } from "@/lib/christmas/offeringDetail";

type Rs = {
  christmasHeading?: string;
  christmasEmpty?: string;
  christmasUntitled?: string;
  christmasPerPerson?: string;
  christmasPerPackage?: string;
  christmasOpenImages?: string;
  availability?: Record<string, string>;
};

export function ChristmasMenuSection({
  offerings,
  restaurantName,
  highlightOfferingId,
  rs,
}: {
  offerings: ShareChristmasOfferingSummary[];
  restaurantName: string | null;
  highlightOfferingId?: string | null;
  rs: Rs;
}) {
  if (offerings.length === 0) {
    return (
      <p className="text-sm text-gray-600 dark:text-gray-400 text-center py-6">
        {rs.christmasEmpty ?? "No hay paquetes de Navidad públicos."}
      </p>
    );
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
        {rs.christmasHeading ?? "Menús de Navidad"}
      </h2>
      <ul className="space-y-3">
        {offerings.map((offering) => {
          const price = formatOfferingPrice(offering.priceEur, offering.currency);
          const availKey = offering.derivedAvailability ?? "";
          const availLabel =
            availKey && rs.availability?.[availKey]
              ? rs.availability[availKey]
              : null;
          const href = buildWebOfferingPath(offering.id);
          const highlight = highlightOfferingId === offering.id;
          const title =
            offering.packageTitle?.trim() ||
            rs.christmasUntitled ||
            "Paquete de Navidad";
          return (
            <li key={offering.id}>
              <Link
                href={href}
                className={`flex gap-4 rounded-lg border p-3 hover:shadow-md transition-shadow ${
                  highlight
                    ? "border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20"
                    : "border-gray-100 dark:border-gray-800"
                }`}
              >
                {offering.coverImageUrl ? (
                  <div className="relative w-20 h-20 sm:w-24 sm:h-24 shrink-0 rounded-md overflow-hidden bg-gray-100">
                    <Image
                      src={offering.coverImageUrl}
                      alt={
                        restaurantName
                          ? `${title} — ${restaurantName}`
                          : title
                      }
                      width={96}
                      height={96}
                      className="object-cover w-full h-full"
                      unoptimized
                    />
                  </div>
                ) : (
                  <div className="w-20 h-20 sm:w-24 sm:h-24 shrink-0 rounded-md bg-gray-100 dark:bg-gray-800" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 dark:text-white truncate">
                    {title}
                  </p>
                  {price && (
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                      <span className="inline-block bg-orange-500 text-white text-sm px-2.5 py-0.5 rounded-full font-semibold">
                        {price}
                      </span>
                      <span className="ml-2 text-xs text-gray-500">
                        {offering.pricePerPerson
                          ? rs.christmasPerPerson ?? "por persona"
                          : rs.christmasPerPackage ?? "por menú"}
                      </span>
                    </p>
                  )}
                  {availLabel && (
                    <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mt-1.5">
                      {availLabel}
                    </p>
                  )}
                  <p className="text-sm text-blue-600 mt-2">
                    {rs.christmasOpenImages ?? "Ver fotos del menú"} →
                  </p>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
